import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Box, Stack, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Iconify } from 'src/components';

function Pill({ children, onClear }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 0.75,
        borderRadius: 1.5,
        border: (theme) => `dashed 1px ${theme.palette.divider}`,
        bgcolor: (theme) => (theme.palette.mode === 'light' ? 'background.paper' : 'background.default'),
      }}
    >
      {children}
      {!!onClear && (
        <IconButton size="small" onClick={onClear} sx={{ p: 0.25 }}>
          <Iconify icon="eva:close-fill" width={16} height={16} />
        </IconButton>
      )}
    </Box>
  );
}

export default function CalendarFiltersResult({
  filters,
  totalResults,
  onReset,
  onClearDate,
  onClearColors,
  loading,
}) {
  const hasDate = Boolean(filters?.startDate && filters?.endDate);
  const hasColors = Boolean(filters?.colors?.length);

  const renderDate = () => {
    const s = filters?.startDate ? format(filters.startDate, 'd MMM yyyy') : '';
    const e = filters?.endDate ? format(filters.endDate, 'd MMM yyyy') : '';
    return (
      <Pill onClear={onClearDate}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Date:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {s} – {e}
        </Typography>
      </Pill>
    );
  };

  const renderColors = () => (
    <Pill onClear={onClearColors}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Colors:
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{filters.colors.length}</Typography>
    </Pill>
  );

  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
        {loading ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={14} thickness={5} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading…</Typography>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {totalResults} results found
          </Typography>
        )}

        {hasDate && renderDate()}
        {hasColors && renderColors()}

        {(hasDate || hasColors) && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ ml: 1 }}>
            <Tooltip title="Clear all">
              <IconButton size="small" onClick={onReset} sx={{ color: 'error.main' }}>
                <Iconify icon="solar:trash-bin-minimalistic-2-bold-duotone" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" sx={{ color: 'error.main', cursor: 'pointer' }} onClick={onReset}>
              Clear
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

CalendarFiltersResult.propTypes = {
  filters: PropTypes.shape({ colors: PropTypes.array, startDate: PropTypes.any, endDate: PropTypes.any }),
  totalResults: PropTypes.number,
  onReset: PropTypes.func,
  onClearDate: PropTypes.func,
  onClearColors: PropTypes.func,
  loading: PropTypes.bool,
};
